import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Box,
  Tab,
  Tabs,
  Grid,
  Button,
  CircularProgress,
} from '@material-ui/core';

import { useSearchParams } from 'react-router-dom';
import StopIcon from '@material-ui/icons/Stop';
import { useStyles } from '../../styles';
import '../../styles/projectCard.css';
import '../../styles/executionCenter.css';
import type {
  ExecutionDashboard,
  IqeProject,
  IqeProjectStatus,
  EnhancementTestPlanSelection,
} from '../../interfaces/ui';
import { useActivityLog } from '../../hooks/useActivityLog';
import { useExecutionResults } from '../../hooks/useExecutionResults';
import { useExecutionTestCases } from '../../hooks/useExecutionTestCases';
import { useProjectRuns } from '../../hooks/useProjectRuns';
import {
  getLatestRun,
  getProjectExecutionActionState,
  getModeFromOperation,
} from '../../utils/projectExecutionLifecycle';
import type { TestExecutionActionKey } from '../../utils/projectExecutionLifecycle';
import { TRIGGER_OVERLAY_TEXT } from '../../constants';
import ProjectContextHeader from './ProjectContextHeader';
import ExecutionResultsTab from './ExecutionResultsTab';
import ActivityLogTab from './ActivityLogTab';
import TestCasesTab from './TestCasesTab';
import TestExecutionActionDropdown from '../../common/TestExecutionActionDropdown';
import LoadingState from '../../common/LoadingState';
import RunHistoryTab from './RunHistoryTab';
import { AdminActionHint } from '../../common/AdminActionHint';
import BackButton from '../../../../../utils/backButton';
import { HelpComponent } from '../../../../../utils/helpComponent';
import HELP_URL from '../../../../../utils/helpLinkConstant';
import { EnhanceTestPlanModal } from '../ProjectCard/EnhanceTestPlanModal';
import { StopRunDialog } from './StopRunDialog';
import {
  isCreateFailed,
  isCreateInProgress,
  isCreateLoginFailed,
  isCreateSuccessful,
  isGenerateFailed,
  isGenerateOrEnhanceInProgress,
  isGenerateStopped,
  isProjectOperationInProgress,
  isProjectOperationStopped,
  isProjectOperationStopping,
  isProjectOperationSuccessful,
} from '../../utils/projectStatusChecks';

type ArtifactTab = 'activity' | 'results' | 'cases' | 'history';

interface ExecutionCenterProps {
  isToolAdmin: boolean;
  isOpen?: boolean;
  mode?: string;
  isPerformingAction?: boolean;
  apmNumber: string;
  projectName: string;
  appUrl: string;
  projectId: string;
  project?: IqeProject;
  runId?: string;
  environment: string;
  testPlanName: string;
  testSuiteName: string;
  projectStatus: IqeProjectStatus;
  frontendRepositoryName: string;
  frontendRepositoryUrl: string;
  frontendRepositoryBranch: string;
  onViewDetailedResults?: (projectId: string) => void;
  onRefreshStatus?: () => Promise<void>;
  onStartExecution?: (
    action: TestExecutionActionKey,
    userPrompt?: string,
    enhancementSelection?: EnhancementTestPlanSelection,
  ) => Promise<string | null>;
  onStopRun?: () => Promise<boolean>;
  isStoppingRun?: boolean;
}

export const ExecutionCenter = ({
  isToolAdmin,
  isOpen = true,
  mode,
  isPerformingAction = false,
  apmNumber,
  projectName,
  appUrl,
  projectId,
  project,
  runId,
  environment,
  testPlanName,
  testSuiteName,
  projectStatus,
  frontendRepositoryName,
  frontendRepositoryUrl,
  frontendRepositoryBranch,
  onViewDetailedResults,
  onRefreshStatus,
  onStartExecution,
  onStopRun,
  isStoppingRun = false,
}: ExecutionCenterProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const executionMode =
    searchParams.get('mode') ||
    mode ||
    getModeFromOperation(project?.operation);
  const isTestPlanChangingOperation =
    executionMode === 'generate' || executionMode === 'enhance';
  const [showEnhanceModal, setShowEnhanceModal] = useState(false);
  const [showStopRunDialog, setShowStopRunDialog] = useState(false);

  const isProjectSuccessful = isProjectOperationSuccessful(projectStatus);
  const isProjectInProgress = isProjectOperationInProgress(projectStatus);
  const isProjectStopping = isProjectOperationStopping(projectStatus);
  const isStopPending = isStoppingRun || isProjectStopping;

  // Determine default tab based on project status
  const getDefaultTab = (): ArtifactTab => {
    // Generate Failed status always goes to Test Cases
    if (isGenerateFailed(projectStatus)) {
      return 'cases';
    }
    // GENERATING or EXECUTING → Activity Log (to see streaming activity)
    if (isProjectInProgress) {
      return 'activity';
    }
    // Completed statuses → Test Cases
    if (isProjectSuccessful) {
      return 'cases';
    }
    // Default to Test Cases for other cases
    return 'cases';
  };

  const defaultTab: ArtifactTab = getDefaultTab();
  const activeTab = (searchParams.get('tab') as ArtifactTab) || defaultTab;
  const setActiveTab = useCallback(
    (tab: ArtifactTab) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Refs captured at mount from URL — never included in effect deps
  const isExecuteSuiteModeRef = useRef(searchParams.get('mode') === 'execute');

  const [isActivityComplete, setIsActivityComplete] = useState(false);
  const [isStoppedByUser, setIsStoppedByUser] = useState(false);
  const [streamStarted, setStreamStarted] = useState(() => isProjectInProgress);

  const executionClickedRef = useRef(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastAction, setLastAction] =
    useState<TestExecutionActionKey>('GENERATE');
  const fetchedResultsRef = useRef<string | null>(null);
  const fetchedCasesRef = useRef<string | null>(null);
  const streamedRunIdRef = useRef<string | null>(null);
  const pendingStreamRunIdRef = useRef<string | null>(null);
  const autoNavigateRunIdRef = useRef<string | null>(null);
  const activityLog = useActivityLog();
  const executionResults = useExecutionResults();
  const executionTestCases = useExecutionTestCases();
  const projectRuns = useProjectRuns();

  const hasFreshRunToSyncRef = useRef(false);
  // True for the entire stream lifetime — stable across all SSE events
  const isExecuting = streamStarted && !isActivityComplete;
  const recentActivityLog = Array.isArray(project?.recentActivityLog)
    ? project.recentActivityLog
    : [];
  const isHistoricalActivityView = !isProjectInProgress && !isExecuting;
  const displayedActivityLog =
    activityLog.data.length > 0 || !isHistoricalActivityView
      ? activityLog.data
      : recentActivityLog;
  // Historical log has no live stream to "close", so treat it as already complete
  const effectiveIsActivityComplete = isHistoricalActivityView
    ? true
    : isActivityComplete;
  const latestRun = getLatestRun(project?.runs);
  const historicalExecutionResults: ExecutionDashboard | null =
    !isProjectInProgress && !isExecuting && latestRun
      ? {
          status: latestRun.status,
          dashboardUrl: latestRun.dashboardUrl?.trim() || '',
          reportUrl: latestRun.reportUrl?.trim() || '',
        }
      : null;
  const executionResultsData =
    !isProjectInProgress && !isExecuting
      ? executionResults.data ?? historicalExecutionResults
      : executionResults.data;
  // Button stays visible once test cases load; disabled state controls interactivity
  const shouldShowExecuteButton =
    isExecuteSuiteModeRef.current &&
    !executionTestCases.error &&
    executionTestCases.data.length > 0;

  const classes = useStyles();

  const hasPreviousRuns = project?.runs && project.runs.length > 0;

  const isExecutionInProgress = isProjectInProgress;
  const isActiveRun = Boolean(
    onStopRun && runId && runId !== 'latest' && isProjectInProgress,
  );

  const isExecutingWithPreviousRuns = isProjectInProgress && hasPreviousRuns;
  const hasActivityAccessToken = Boolean(activityLog.accessToken);

  const { displayVersion, options } = project
    ? getProjectExecutionActionState(project.runs, isExecutionInProgress)
    : { displayVersion: null, options: [] };

  const handleActionSelect = (action: TestExecutionActionKey) => {
    if (!isToolAdmin) return;
    if (action === 'GENERATE' || action === 'RUN') {
      handleStartExecution(action);
    } else if (action === 'ENHANCE') {
      setShowEnhanceModal(true);
    }
  };

  const handleRunTests = (
    userPrompt: string,
    enhancementSelection?: EnhancementTestPlanSelection,
  ) => {
    handleStartExecution('ENHANCE', userPrompt, enhancementSelection);
  };

  const handleStopRunConfirm = useCallback(async () => {
    if (!isToolAdmin || !onStopRun || !isActiveRun || isStoppingRun) return;

    setShowStopRunDialog(false);
    const stopped = await onStopRun();
    if (stopped) {
      setIsStoppedByUser(true);
      setIsActivityComplete(true);
      setStreamStarted(false);
      setIsSubmitting(false);
      executionClickedRef.current = false;
      autoNavigateRunIdRef.current = runId || null;
      hasFreshRunToSyncRef.current = true;
    }
  }, [
    activityLog.reset,
    isActiveRun,
    isStoppingRun,
    isToolAdmin,
    onStopRun,
    runId,
  ]);

  useEffect(() => {
    if (!isOpen || !projectId || !runId) return;
    if (!hasActivityAccessToken) return;

    // Don't stream if runId is a placeholder (for Ready status with no runs)
    if (runId === 'latest') return;

    const isNewlyTriggeredRun = pendingStreamRunIdRef.current === runId;
    if (!isProjectInProgress && !isNewlyTriggeredRun) {
      return;
    }

    // A run may only be streamed once. Tab navigation must not restart it.
    if (streamedRunIdRef.current === runId) {
      return;
    }
    streamedRunIdRef.current = runId;
    pendingStreamRunIdRef.current = null;
    autoNavigateRunIdRef.current = runId;
    setStreamStarted(true);

    setIsActivityComplete(false);
    fetchedResultsRef.current = null;
    activityLog.reset();
    executionResults.reset();

    // All other paths (generate flow, or execute suite mode refreshing at activity tab)
    activityLog.streamActivityLog(
      { apmNumber, projectId, runId, type: 'activity' },
      async () => {
        await onRefreshStatus?.();
        setIsActivityComplete(true);
        setStreamStarted(false);
        setIsSubmitting(false);
        executionClickedRef.current = false;
        hasFreshRunToSyncRef.current = true;
        if (isTestPlanChangingOperation) {
          fetchedCasesRef.current = null;
        }
      },
    );
  }, [
    activityLog.reset,
    activityLog.streamActivityLog,
    apmNumber,
    executionResults.reset,
    isProjectInProgress,
    isTestPlanChangingOperation,
    isOpen,
    projectId,
    runId,
    hasActivityAccessToken,
    onRefreshStatus,

    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  useEffect(() => {
    return () => {
      activityLog.reset();
    };
  }, [activityLog.reset]);

  useEffect(() => {
    if (!apmNumber || !projectId) return;
    projectRuns.fetchRuns(apmNumber, projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apmNumber, projectId]);

  useEffect(() => {
    if (activeTab !== 'history' || !hasFreshRunToSyncRef.current) return;
    hasFreshRunToSyncRef.current = false;
    projectRuns.fetchRuns(apmNumber, projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (
      activeTab !== 'results' ||
      isStoppedByUser ||
      isStopPending ||
      isProjectOperationStopped(projectStatus) ||
      !projectId ||
      !runId ||
      !isActivityComplete ||
      fetchedResultsRef.current === runId
    ) {
      return;
    }

    fetchedResultsRef.current = runId;
    executionResults.fetchResults({
      apmNumber,
      projectId,
      runId,
      type: 'dashboard',
    });
  }, [
    activeTab,
    apmNumber,
    executionResults.fetchResults,
    isActivityComplete,
    isProjectOperationStopped,
    isStopPending,
    isStoppedByUser,
    projectId,
    projectStatus,
    runId,
  ]);

  useEffect(() => {
    if (autoNavigateRunIdRef.current !== runId || !isActivityComplete) {
      return;
    }

    if (activeTab === 'activity') {
      setActiveTab('results');
    }
    autoNavigateRunIdRef.current = null;
  }, [isActivityComplete, activeTab, runId]);

  useEffect(() => {
    if (
      executionMode !== 'execute' ||
      !projectId ||
      !runId ||
      runId === 'latest' ||
      !isActivityComplete ||
      executionTestCases.data.length > 0 ||
      executionTestCases.loading ||
      fetchedCasesRef.current === runId
    ) {
      return;
    }

    fetchedCasesRef.current = runId;
    executionTestCases.fetchTestCases({
      apmNumber,
      projectId,
      runId,
      type: 'testplan',
    });
  }, [
    apmNumber,
    executionMode,
    executionTestCases.data.length,
    executionTestCases.fetchTestCases,
    executionTestCases.loading,
    isActivityComplete,
    projectId,
    runId,
  ]);

  useEffect(() => {
    if (!activityLog.error) return;

    setStreamStarted(false);
    setIsSubmitting(false);
    executionClickedRef.current = false;
  }, [activityLog.error]);

  useEffect(() => {
    if (
      activeTab !== 'cases' ||
      !projectId ||
      !runId ||
      runId === 'latest' ||
      (isProjectInProgress &&
        !isActivityComplete &&
        executionTestCases.data.length > 0) ||
      (!isTestPlanChangingOperation && executionTestCases.data.length > 0) ||
      fetchedCasesRef.current === runId
    ) {
      return;
    }
    fetchedCasesRef.current = runId;
    executionTestCases.fetchTestCases({
      apmNumber,
      projectId,
      runId,
      type: 'testplan',
    });
  }, [
    activeTab,
    apmNumber,
    executionTestCases.data.length,
    executionTestCases.fetchTestCases,
    isActivityComplete,
    isProjectInProgress,
    isTestPlanChangingOperation,
    projectId,
    runId,
  ]);

  const handleStartExecution = useCallback(
    async (
      action: TestExecutionActionKey = 'GENERATE',
      userPrompt = '',
      enhancementSelection?: EnhancementTestPlanSelection,
    ) => {
      if (!isToolAdmin) return;
      if (executionClickedRef.current || isPerformingAction) return; // synchronous guard against duplicate clicks
      if (!hasActivityAccessToken) return;
      executionClickedRef.current = true;
      autoNavigateRunIdRef.current = null;
      setIsActivityComplete(false);
      setIsStoppedByUser(false);
      setLastAction(action);
      setIsSubmitting(true); // disable the button on next render before streamStarted commits

      if (onStartExecution) {
        const newRunId = await onStartExecution(
          action,
          userPrompt,
          enhancementSelection,
        );
        pendingStreamRunIdRef.current = newRunId;
        setIsSubmitting(false);
        if (!newRunId) {
          executionClickedRef.current = false;
        }
        return;
      }
      if (!projectId || !runId) return;

      pendingStreamRunIdRef.current = runId;
      setStreamStarted(true);
      setIsSubmitting(false);
      setActiveTab('activity');
    },
    [
      onStartExecution,
      isToolAdmin,
      hasActivityAccessToken,
      isPerformingAction,
      projectId,
      runId,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
  );

  const refreshActivityLog = useCallback(() => {
    if (!projectId || !runId) return;
    if (!hasActivityAccessToken) return;

    // Refresh is an explicit same-run metrics request and must stay on Activity Log.
    autoNavigateRunIdRef.current = null;
    setIsActivityComplete(false);
    setStreamStarted(true);
    activityLog.streamActivityLog(
      { apmNumber, projectId, runId, type: 'activity' },
      async () => {
        await onRefreshStatus?.();
        setIsActivityComplete(true);
        setStreamStarted(false);
      },
    );
  }, [
    activityLog.streamActivityLog,
    apmNumber,
    hasActivityAccessToken,
    projectId,
    runId,
    onRefreshStatus,

    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  if (!isOpen) return null;

  // Failed status checks
  const isValidationFailed =
    isCreateFailed(projectStatus) || isCreateLoginFailed(projectStatus);
  // Only a creation/validation failure has no run to show; GENERATE/EXECUTE/ENHANCE failures still have prior run data and actions.
  const isAnyFailedStatus = isValidationFailed;
  const shouldShowGenerateAction =
    isCreateSuccessful(projectStatus) ||
    isGenerateFailed(projectStatus) ||
    isGenerateStopped(projectStatus);

  // Show tabs and content if we have a runId with displayVersion, OR if project is not in an error/in-progress state with no previous runs
  const hasRun = runId && displayVersion;
  const isFailureOrInProgress =
    isCreateFailed(projectStatus) ||
    isCreateLoginFailed(projectStatus) ||
    isCreateInProgress(projectStatus) ||
    isProjectInProgress ||
    isGenerateFailed(projectStatus);
  const isCompletedOrSuccessful = isProjectOperationSuccessful(projectStatus);
  const hasNoPreviousRuns = !project?.runs || project.runs.length === 0;
  const isGeneratingOrExecuting = isProjectInProgress;
  // Show tabs if we have a valid run, or if project is ready (not in failure/in-progress state) with no runs, or if generating/executing, or if completed/successful
  // BUT hide tabs for any failed status
  const shouldShowTabs =
    !isAnyFailedStatus &&
    (hasRun ||
      (!isFailureOrInProgress && hasNoPreviousRuns) ||
      isGeneratingOrExecuting ||
      isCompletedOrSuccessful);

  const showTriggerOverlay = isSubmitting;
  const shouldShowStopButton =
    (streamStarted && !isActivityComplete && activityLog.data.length > 0) ||
    isStopPending;
  const triggerOverlayText =
    TRIGGER_OVERLAY_TEXT[lastAction] ?? TRIGGER_OVERLAY_TEXT.GENERATE;
  const hasRestrictedAction =
    !isToolAdmin &&
    ((shouldShowGenerateAction && Boolean(onStartExecution)) ||
      (!shouldShowGenerateAction &&
        Boolean(displayVersion) &&
        options.length > 0) ||
      shouldShowStopButton);

  return (
    <main>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <BackButton />
        <HelpComponent helpUrl={HELP_URL.IQE_AGENT_TEST_EXECUTION_PAGE} />
      </Grid>
      <Box className={`ec-center ${classes.executionCenter}`}>
        <ProjectContextHeader
          projectName={projectName}
          environment={environment}
          appUrl={appUrl}
          testPlanName={testPlanName}
          testSuiteName={testSuiteName}
          lastRunDate={displayVersion?.startedAt || null}
          testPlanVersion={
            displayVersion
              ? `v${displayVersion.transactionId.slice(-6).toUpperCase()}`
              : null
          }
          testPlanUrl={project?.testPlanUrl}
          projectStatus={projectStatus}
          frontendRepositoryName={frontendRepositoryName}
          frontendRepositoryUrl={frontendRepositoryUrl}
          frontendRepositoryBranch={frontendRepositoryBranch}
          hasPreviousRuns={hasPreviousRuns}
        />
        {(shouldShowTabs || shouldShowStopButton) && (
          <Box className="ec-action-controls">
            <Box className="ec-action-buttons-row">
              {shouldShowGenerateAction ? (
                onStartExecution && (
                  <Box className="ec-action-control">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleStartExecution('GENERATE')}
                      disabled={
                        !isToolAdmin ||
                        isSubmitting ||
                        isPerformingAction ||
                        activityLog.loading ||
                        isProjectInProgress ||
                        isProjectStopping ||
                        isStoppingRun
                      }
                      className="ec-action-btn"
                    >
                      Generate & Execute
                    </Button>
                    {(isSubmitting ||
                      isPerformingAction ||
                      activityLog.loading) && (
                      <CircularProgress
                        size={20}
                        className="ec-action-progress"
                      />
                    )}
                  </Box>
                )
              ) : displayVersion && options.length > 0 ? (
                <Box className="ec-action-control">
                  <TestExecutionActionDropdown
                    buttonClassName=""
                    menuClassName="pc-action-dropdown-menu"
                    menuItemClassName="pc-action-dropdown-item"
                    disabled={
                      !isToolAdmin ||
                      isExecutingWithPreviousRuns ||
                      isProjectStopping ||
                      !displayVersion ||
                      isPerformingAction ||
                      isSubmitting ||
                      activityLog.loading ||
                      isStoppingRun
                    }
                    options={options}
                    onSelect={handleActionSelect}
                  />
                  {(isSubmitting ||
                    isPerformingAction ||
                    activityLog.loading) && (
                    <CircularProgress
                      size={20}
                      className="ec-action-progress"
                    />
                  )}
                </Box>
              ) : null}
              {shouldShowStopButton && (
                <Button
                  variant="contained"
                  className="ec-action-btn ec-stop-run-btn"
                  startIcon={
                    isStopPending ? (
                      <CircularProgress
                        size={16}
                        className="ec-stop-run-spinner"
                      />
                    ) : (
                      <StopIcon />
                    )
                  }
                  disabled={
                    !isToolAdmin ||
                    !isActiveRun ||
                    isStopPending ||
                    activityLog.data.length === 0
                  }
                  onClick={() => setShowStopRunDialog(true)}
                  aria-label={isStopPending ? 'Stopping run' : 'Stop run'}
                >
                  {isStopPending ? 'Stopping...' : 'Stop Run'}
                </Button>
              )}
            </Box>
            {hasRestrictedAction && (
              <AdminActionHint
                isToolAdmin={isToolAdmin}
                className="ec-action-permission-hint"
              />
            )}
          </Box>
        )}
        {shouldShowTabs && (
          <Box className="ec-content">
            <Box className="ec-artifacts-section">
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value as ArtifactTab)}
                indicatorColor="primary"
                textColor="primary"
                className="ec-tabs"
              >
                <Tab label="Activity Log" value="activity" className="ec-tab" />
                <Tab label="Test Cases" value="cases" className="ec-tab" />
                <Tab label="Results" value="results" className="ec-tab" />
                <Tab label="Run History" value="history" className="ec-tab" />
              </Tabs>

              <LoadingState
                open={showTriggerOverlay}
                primaryText={triggerOverlayText.primary}
                secondaryText={triggerOverlayText.secondary}
              />

              {activeTab === 'activity' && (
                <ActivityLogTab
                  activityLog={displayedActivityLog}
                  loading={activityLog.loading}
                  error={activityLog.error}
                  onRefresh={isExecuting ? refreshActivityLog : undefined}
                  executionMode={
                    executionMode === 'execute' ? 'execute' : 'generate'
                  }
                  showEmptyState={
                    !isFailureOrInProgress &&
                    !isProjectInProgress &&
                    displayedActivityLog.length === 0
                  }
                  isSubmitting={isSubmitting}
                  actions={null}
                  projectStatus={projectStatus}
                  isActivityComplete={effectiveIsActivityComplete}
                />
              )}
              {activeTab === 'results' && (
                <ExecutionResultsTab
                  data={executionResultsData}
                  loading={executionResults.loading}
                  error={executionResults.error}
                  projectId={projectId}
                  onViewDetailedResults={onViewDetailedResults}
                  projectStatus={projectStatus}
                  executionMode={executionMode}
                  isStoppedOverride={isStoppedByUser}
                />
              )}
              {activeTab === 'cases' && (
                <TestCasesTab
                  testSuites={executionTestCases.data}
                  loading={executionTestCases.loading}
                  error={executionTestCases.error}
                  isGenerating={isGenerateOrEnhanceInProgress(projectStatus)}
                  showExecuteButton={shouldShowExecuteButton}
                  executeButtonDisabled={
                    executionTestCases.loading || isSubmitting || isExecuting
                  }
                  projectStatus={projectStatus}
                  currentRunId={runId}
                  actions={
                    <>
                      {/* Future actions: implement and wire handlers before enabling.
                      <Button
                        variant="contained"
                        className="ec-action-btn ec-tab-action-btn pc-btn-tinted"
                        color="primary"
                        startIcon={<HealingIcon />}
                      >
                        Heal failed tests
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        className="ec-action-btn ec-tab-action-btn pc-btn-tinted"
                        startIcon={<CallMergeIcon />}
                      >
                        Deduplicate tests
                      </Button>
                      */}
                    </>
                  }
                />
              )}
              {activeTab === 'history' && (
                <RunHistoryTab
                  apmNumber={apmNumber}
                  project={project || null}
                  runs={
                    projectRuns.error ? project?.runs ?? [] : projectRuns.runs
                  }
                  runsLoading={projectRuns.loading}
                  runsError={projectRuns.error}
                  onRetry={() => projectRuns.fetchRuns(apmNumber, projectId)}
                  isToolAdmin={isToolAdmin}
                />
              )}
            </Box>
          </Box>
        )}

        <EnhanceTestPlanModal
          open={showEnhanceModal}
          apmNumber={apmNumber}
          onClose={() => setShowEnhanceModal(false)}
          onRunTests={handleRunTests}
        />
        <StopRunDialog
          open={showStopRunDialog}
          onCancel={() => setShowStopRunDialog(false)}
          onConfirm={handleStopRunConfirm}
          executionMode={executionMode}
        />
      </Box>
    </main>
  );
};
